const GAUGE_SEGMENTS = [
  { from: 0, to: 25, color: "#f87171" },
  { from: 25, to: 50, color: "#fb923c" },
  { from: 50, to: 75, color: "#facc15" },
  { from: 75, to: 100, color: "#4ade80" },
];

const GAUGE_TICKS = [0, 25, 50, 75, 100];

function pciNeedleColor(value: number): string {
  if (value >= 75) return "#16a34a";
  if (value >= 50) return "#ca8a04";
  if (value >= 25) return "#ea580c";
  return "#dc2626";
}

interface GaugeChartProps {
  value: number;
  date?: string;
  previousDate?: string;
}

export const GaugeChart: React.FC<GaugeChartProps> = ({
    value,
    date = "Last updated 2/20/2026",
}) => {
    const cx = 130, cy = 120, r = 90;

    const pciToAngle = (v: number) => 180 - (v / 100) * 180;
    const angle = pciToAngle(value);
    const rad = (angle * Math.PI) / 180;

    const tipX = cx + r * 0.8 * Math.cos(rad);
    const tipY = cy - r * 0.8 * Math.sin(rad);
    const bL = {
        x: cx + 7 * Math.cos(rad - Math.PI / 2),
        y: cy - 7 * Math.sin(rad - Math.PI / 2),
    };
    const bR = {
        x: cx + 7 * Math.cos(rad + Math.PI / 2),
        y: cy - 7 * Math.sin(rad + Math.PI / 2),
    };

    const polar = (a: number) => ({
        x: cx + r * Math.cos((a * Math.PI) / 180),
        y: cy - r * Math.sin((a * Math.PI) / 180),
    });

    const arcPath = (fromPci: number, toPci: number) => {
        const s = polar(180 - (fromPci / 100) * 180);
        const e = polar(180 - (toPci / 100) * 180);
        return `M ${s.x} ${s.y} A ${r} ${r} 0 0 1 ${e.x} ${e.y}`;
    };

    const needleColor = pciNeedleColor(value);

    return (
        <div className="flex flex-col items-center">
            <svg width={260} height={170} viewBox="0 0 260 170">
                {GAUGE_SEGMENTS.map((seg) => (
                <path
                    key={seg.from}
                    d={arcPath(seg.from, seg.to)}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={20}
                    strokeLinecap="butt"
                />
                ))}

                {GAUGE_TICKS.map((t) => {
                const a = 180 - (t / 100) * 180;
                const ar = (a * Math.PI) / 180;
                const inner = { x: cx + (r - 13) * Math.cos(ar), y: cy - (r - 13) * Math.sin(ar) };
                const outer = { x: cx + (r + 3) * Math.cos(ar),  y: cy - (r + 3) * Math.sin(ar)  };
                const lbl   = { x: cx + (r + 18) * Math.cos(ar), y: cy - (r + 18) * Math.sin(ar) };
                return (
                    <g key={t}>
                    <line
                        x1={inner.x} y1={inner.y}
                        x2={outer.x} y2={outer.y}
                        stroke="#9ca3af" strokeWidth={1.5}
                    />
                    <text
                        x={lbl.x} y={lbl.y}
                        textAnchor="middle" dominantBaseline="middle"
                        fontSize={9} fill="#6b7280"
                    >
                        {t}
                    </text>
                    </g>
                );
                })}

                <polygon
                points={`${tipX},${tipY} ${bL.x},${bL.y} ${bR.x},${bR.y}`}
                fill="#1f2937"
                />
                <circle cx={cx} cy={cy} r={8} fill="#1f2937" />
                <circle cx={cx} cy={cy} r={3} fill="white" />

                <text
                x={cx} y={cy + 25}
                textAnchor="middle"
                fontSize={22}
                fontWeight="700"
                fill={needleColor}
                >
                PCI {value}
                </text>
                <text
                x={cx} y={cy + 40}
                textAnchor="middle"
                fontSize={10}
                fill="#9ca3af"
                >
                {date}
                </text>
            </svg>
        </div>
    );
};

